import { IAppContext } from '../context/DefaultAppState';
import * as _ from 'lodash';
import { DirectoryListType } from '../components/directory-list/DirectoryList';
import IGenericResult from '../interfaces/IGenericResult';
import IDistance from '../interfaces/IDistance';
import ISearch from '../interfaces/ISearch';


/**
 * @param {Boolean} shouldShowMore Whether or not more than the minimum number of specialty groups should display.
 * @param {IAppContext} appContext The provided application context.
 * @description Determines what specialty groups to display based on what directory list type is currently selected.
 * @returns {String[]} A list of categories (specialty groups).
 */
export const getCategoriesToMap = (shouldShowMore: boolean, appContext: IAppContext, isNotDropDown?: boolean): string[] => {
  let specialties: string[];

  switch (appContext.directoryListType) {
    case DirectoryListType.doctor:
      specialties = _.sortBy(
        _.uniqBy(appContext.doctorSpecialties, 'specialtyGroup').map((ds) => _.startCase(ds.specialtyGroup.toLowerCase())),
        (x: any) => x.toLowerCase()
      );
      break;
    case DirectoryListType.pharmacy:
      specialties = _.sortBy(
        _.uniq(appContext.pharmacySpecialties).map((ps) => _.startCase(ps.toLowerCase())),
        (x: any) => x.toLowerCase()
      );
      break;
    case DirectoryListType.facility:
      specialties = _.sortBy(
        _.uniqBy(appContext.facilitySpecialties, 'specialtyGroup').map((fs) => _.startCase(fs.specialtyGroup.toLowerCase())),
        (x: any) => x.toLowerCase()
      );
      break;
    case DirectoryListType.dentist:
      specialties = _.sortBy(
        _.uniqBy(appContext.dentistSpecialties, 'specialtyGroup').map((fs) => _.startCase(fs.specialtyGroup.toLowerCase())),
        (x: any) => x.toLowerCase()
      );
      break;
    case DirectoryListType.all:
      const doctorSpecialties = appContext.doctorSpecialties || [];

      const pharmacySpecialties = appContext.pharmacySpecialties ? appContext.pharmacySpecialties.map((ps) => ({ specialtyGroup: ps })) : [];

      const facilitySpecialties = appContext.facilitySpecialties || [];

      specialties = _.sortBy(
        _.uniqBy([...doctorSpecialties, ...facilitySpecialties, ...pharmacySpecialties], 'specialtyGroup').map((s) => _.startCase(s.specialtyGroup.toLowerCase())),
        (x: any) => x.toLowerCase()
      );
      break;
    default:
      specialties = [];
      break;
  }

  if (specialties.length && !isNotDropDown) {
    specialties.unshift('All Specialties');
  }

  const numToMap = shouldShowMore ? specialties.length : 9;
  return _.take(specialties, numToMap);
};

/**
 * @param {IAppContext} appContext - The provided app context.
 * @param {String} state - The state name selected from the control.
 * @description Sets the disclaimer url in the header according to the selected plan state.
 */
export const UpdateDisclaimerUrl = (appContext: IAppContext, state: string) => {
  const plan = appContext.plansData.find((p) => p.state === state);

  if (plan) {
    appContext.updateDisclaimerUrl(plan.ipdDisclaimerLink);
    appContext.updatePlanCode(plan.contractId);
    appContext.updatePlan(plan);
  }
};

/**
 * @param toUrl - The Url to navigate to.
 * @returns {String} path
 * @description Returns path to state selection, if no state was selected in the drop down.
 */
export const RouteWithState = (toUrl: string, appContext: IAppContext, specialty?: string) => {
  if (specialty) {
    appContext.updateSelectedSpecialty(specialty);
  }
  return appContext.planState ? toUrl : `/state-selection`;
};

export const renderCmsIdNumber = (appContext: IAppContext) => {
  if (appContext.plan && appContext.planState) {
    const planCode = appContext.plan.contractId;
    appContext.updatePlanCode(planCode);
    return appContext.planCode;
  }
  return '';
};

/**
 * @param rawResults - Unmapped Facility, Pharmacy, or Doctor Results
 * @returns {IGenericResult[]} genericResults
 */
export const mapToGenericResults = (rawResults: any[]): IGenericResult[] => {
  const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };


  let genericResults: IGenericResult[] = [];
  if (rawResults.length) {
    rawResults.map((result) => {
      const latitude = toNum(result.latitude ?? result.Latitude);
      const longitude = toNum(result.longitude ?? result.Longitude);
      if (result.isDoctor) {
        genericResults.push({
          recordId: result.recordId,
          name: `${result.firstName} ${result.lastName}${result.credentials ? ', ' + result.credentials : ''}`,
          locationName: result.practices,
          specialty: result.specialty,
          specialtyGroup: result.specialtyGroup,
          specialties: result.specialties,
          addressLine1: result.addressLine1,
          addressLine2: result.addressLine2,
          zipCode: result.zipCode,
          city: result.city,
          state: result.state,
          phone: result.phone,
          fax: result.fax || 'N/A',
          acceptingNewPatients: result.acceptingNewPatients,
          providerNPI: result.providerNPI,
          boardCertification: result.boardCertification,
          practices: result.practices,
          language: result.language,
          uniqueProviderId: '',
          firstName: result.firstName,
          lastName: result.lastName,
          credentials: '',
          hoursOfOperation: undefined,
          approximateDistance: result.approximateDistance || undefined,
          companyId: null,
          directorySection: null,
          latitude,
          longitude,
        });
      } else if (result.isPharmacy) {
        genericResults.push({
          recordId: result.recordId,
          name: result.pharmacyNameDir,
          locationName: result.pharmacyNameDir,
          specialty: result.pharmacyType,
          specialtyGroup: result.specialtyGroup,
          specialties: result.specialties,
          addressLine1: result.addressLine1,
          addressLine2: result.addressLine2,
          zipCode: result.zipCode,
          city: result.city,
          state: result.state,
          fax: result.fax || 'N/A',
          phone: result.phone,
          providerNPI: result.providerNPI,
          uniqueProviderId: '',
          firstName: '',
          lastName: '',
          credentials: '',
          practices: null,
          language: null,
          hoursOfOperation: undefined,
          approximateDistance: result.approximateDistance || undefined,
          companyId: null,
          boardCertification: undefined,
          directorySection: null,
          latitude,
          longitude,
        });
      } else {
        // Facility
        genericResults.push({
          recordId: result.recordId,
          name: result.name,
          locationName: result.name,
          specialty: result.specialty,
          specialtyGroup: result.specialtyGroup,
          specialties: result.specialties,
          addressLine1: result.addressLine1,
          addressLine2: result.addressLine2,
          zipCode: result.zipCode,
          city: result.city,
          state: result.state,
          fax: result.fax || 'N/A',
          phone: result.phone,
          providerNPI: result.providerNPI,
          uniqueProviderId: '',
          firstName: '',
          lastName: '',
          credentials: '',
          practices: null,
          language: null,
          hoursOfOperation: undefined,
          approximateDistance: result.approximateDistance || undefined,
          companyId: null,
          boardCertification: undefined,
          directorySection: null,
          latitude,
          longitude,
        });
      }
    });
  }

  return genericResults;
};

/**
 *
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 *
 */
export function distanceOptions(): IDistance[] {
  const distanceObject: IDistance[] = [
    {
      name: 'Select Distance',
      distance: -1
    },
    {
      name: '10 miles',
      distance: 10
    },
    {
      name: '25 miles',
      distance: 25
    },
    {
      name: '50 miles',
      distance: 50
    },
    {
      name: '100 miles',
      distance: 100
    }
  ];
  return distanceObject;
}

export const checkValidZip = (zipCode: string) => {
  const regex = /^[0-9]{5}?$/;
  let isValid = regex.test(zipCode);
  return isValid;
};

export const isEmptyZip = (zipCode: string) => {
  if (zipCode.toUpperCase() === 'UNDEFINED') {
    return true;
  }
  if (zipCode === '' || !zipCode) {
    return true;
  }
  return false;
};

export const evaluateIsDisabled = (newSearch: ISearch, location: string | undefined | null, distance: number) => {
  const case1 = { required: true, isValidZIP: true };
  const case2 = { required: true, isValidZIP: false };
  const case3 = { required: false, isValidZIP: false }; //and isEmptyZip returns
  const case4 = { required: false, isValidZIP: true };

  const tempSearch: ISearch = {
    required: newSearch.required,
    isValidZIP: newSearch.isValidZIP
  };

  const jsonSearch = JSON.stringify(tempSearch);
  const jsonCase1 = JSON.stringify(case1);
  const jsonCase2 = JSON.stringify(case2);
  const jsonCase3 = JSON.stringify(case3);
  const jsonCase4 = JSON.stringify(case4);

  if (jsonSearch === jsonCase1) {
    return false;
  } else if (jsonSearch === jsonCase2) {
    return true;
  } else if (jsonSearch === jsonCase3) {
    if (!location || location === '' || (location && isEmptyZip(location))) {
      return false;
    }
    return true;
  } else if (jsonSearch === jsonCase4) {
    return false;
  } else {
    return false;
  }
};
